/**
 * Client-side feature flags for the site-search app, evaluated over the
 * compiled flag artifact served by the backend proxy (same pattern as
 * apps/marketing/src/utils/flags.ts).
 *
 * Everything is lazy: the compiled flag module is only fetched when a page
 * first asks for a flag. The evaluation context uses the signed-in viewer
 * identity from ViewerContext. It uses the app-zero anon ID from
 * domain/app-flags/IDENTITY.md for anonymous viewers.
 *
 * `evaluateFlag` throws on any failure (network, integrity, evaluation);
 * callers keep their default rendering in that case. Client-side only.
 */

import type {Viewer} from "../context/ViewerContext";

import {getAccountsApiBase} from "./apiBase";

const ANON_ID_STORAGE_KEY = "appFlags.anonId";

interface FlagContext {
    readonly kind: "user";
    readonly key: string;
    readonly anonymous?: boolean;
    readonly email?: string;
    readonly handle?: string;
}

interface CompiledFlagModule {
    readonly evaluate: (flagKey: string, context: FlagContext) => {readonly value: unknown};
}

interface FlagArtifactPointer {
    readonly contentHash: string;
}

/**
 * The app-zero anon ID (IDENTITY.md): minted at first touch, latched in
 * localStorage, never re-rolled automatically.
 */
function getAnonId(): string {
    let key = localStorage.getItem(ANON_ID_STORAGE_KEY);
    if (key === null || key === "") {
        key = crypto.randomUUID();
        localStorage.setItem(ANON_ID_STORAGE_KEY, key);
    }
    return key;
}

async function sha1Hex(text: string): Promise<string> {
    const digest = await crypto.subtle.digest("SHA-1", new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}

let modulePromise: Promise<CompiledFlagModule> | undefined;

/**
 * Lazily fetches the current compiled flag artifact (pointer → module),
 * verifies its content hash, and imports it. Cached for the page lifetime.
 */
function loadFlagModule(): Promise<CompiledFlagModule> {
    modulePromise ??= (async () => {
        const base = `${getAccountsApiBase()}/internal-resources/flag-artifacts`;
        const pointerResponse = await fetch(`${base}/pointer`);
        if (!pointerResponse.ok) {
            throw new Error(`pointer fetch failed: ${pointerResponse.status}`);
        }
        const pointer = (await pointerResponse.json()) as FlagArtifactPointer;
        const moduleResponse = await fetch(`${base}/module?hash=${pointer.contentHash}`);
        if (!moduleResponse.ok) {
            throw new Error(`module fetch failed: ${moduleResponse.status}`);
        }
        const source = await moduleResponse.text();
        if ((await sha1Hex(source)) !== pointer.contentHash) {
            throw new Error("module integrity check failed");
        }
        const url = URL.createObjectURL(new Blob([source], {type: "text/javascript"}));
        try {
            return (await import(/* @vite-ignore */ url)) as CompiledFlagModule;
        } finally {
            URL.revokeObjectURL(url);
        }
    })();
    return modulePromise;
}

/**
 * Evaluates a flag against the given viewer. It throws on a failure. Callers
 * keep their default rendering after a failure.
 */
export function createFlagContext(viewer: Pick<Viewer, "uid" | "email" | "handle">): FlagContext {
    if (viewer.uid === null) {
        return {kind: "user", key: getAnonId(), anonymous: true};
    }
    return {
        kind: "user",
        key: viewer.uid,
        ...(viewer.email ? {email: viewer.email} : {}),
        ...(viewer.handle ? {handle: viewer.handle} : {}),
    };
}

export async function evaluateFlag(
    flagKey: string,
    viewer: Pick<Viewer, "uid" | "email" | "handle">,
): Promise<unknown> {
    const module = await loadFlagModule();
    return module.evaluate(flagKey, createFlagContext(viewer)).value;
}
